'use client';

import React, { useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheck, faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';
import { PulseButton } from 'src/components';
import type { RouteStopAddress } from 'src/hooks/routes/types';

interface AddressSelectorProps {
  addresses: RouteStopAddress[];
  selectedAddressKey?: string;
  onSelect: (address: RouteStopAddress) => void;
  onClose: () => void;
}

/**
 * AddressSelector - Dropdown/popover for selecting an address from available options
 */
const AddressSelector: React.FC<AddressSelectorProps> = ({
  addresses,
  selectedAddressKey,
  onSelect,
  onClose,
}) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const formatAddress = (address: RouteStopAddress): string => {
    return [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.state,
      address.zipCode,
    ]
      .filter(Boolean)
      .join(', ');
  };

  const handleSelect = (address: RouteStopAddress) => {
    onSelect(address);
    onClose();
  };

  if (addresses.length === 0) {
    return (
      <div
        className="absolute top-full right-0 -translate-x-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-neutral-200 p-3 z-50"
        style={{ right: 'auto', left: '50%', transform: 'translateX(-90%)' }}
        data-testid="address-selector"
      >
        <p className="text-sm text-neutral-500">No addresses available</p>
      </div>
    );
  }

  return (
    <div
      className="absolute top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-neutral-200 z-50 overflow-hidden"
      style={{ right: 'auto', left: '50%', transform: 'translateX(-90%)' }}
      data-testid="address-selector"
    >
      <div className="p-2 border-b border-neutral-100">
        <p className="text-xs font-medium text-neutral-600">Select Address</p>
      </div>
      <div className="max-h-64 overflow-y-auto">
        {addresses.map((address) => {
          const isSelected = address.addressKey === selectedAddressKey;
          const isHovered = address.addressKey === hoveredKey;

          return (
            <button
              key={address.addressKey}
              onClick={() => handleSelect(address)}
              onMouseEnter={() => setHoveredKey(address.addressKey)}
              onMouseLeave={() => setHoveredKey(null)}
              className={`
                w-full text-left p-3 border-b border-neutral-100 last:border-b-0
                transition-colors cursor-pointer
                ${isSelected ? 'bg-sky-50' : isHovered ? 'bg-neutral-50' : 'bg-white'}
              `}
              aria-label={`Select address: ${formatAddress(address)}`}
              aria-pressed={isSelected}
              data-testid={`address-option-${address.addressKey}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 mt-0.5">
                  <Icon
                    icon={faMapMarkerAlt}
                    className={`w-3 h-3 ${isSelected ? 'text-sky-600' : 'text-neutral-400'}`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {address.practiceName && (
                      <span className="text-sm font-medium text-neutral-900 truncate">
                        {address.practiceName}
                      </span>
                    )}
                    {address.isPrimary && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-sky-100 text-sky-700 rounded">
                        Primary
                      </span>
                    )}
                    {isSelected && (
                      <Icon
                        icon={faCheck}
                        className="flex-shrink-0 w-3 h-3 text-sky-600 ml-auto"
                      />
                    )}
                  </div>
                  <p className="text-xs text-neutral-600 mt-0.5 leading-relaxed">
                    {formatAddress(address)}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="p-2 border-t border-neutral-100 bg-neutral-50">
        <PulseButton
          variant="plain"
          color="neutral"
          size="sm"
          onClick={onClose}
          className="w-full"
          data-testid="address-selector-cancel"
        >
          Cancel
        </PulseButton>
      </div>
    </div>
  );
};

export default AddressSelector;
